import { useCallback, useEffect, useRef, type PointerEvent, type RefObject } from 'react';

export type EditSection = 'crop' | 'background' | 'tone';

function fitDisplayBoxToWrap(
  wrap: HTMLElement | null,
  box: HTMLElement | null,
  pixelWidth: number,
  pixelHeight: number,
) {
  if (!wrap || !box || pixelWidth < 1 || pixelHeight < 1) return;
  const availableW = wrap.clientWidth;
  const availableH = wrap.clientHeight;
  if (availableW < 1 || availableH < 1) return;
  const scale = Math.min(availableW / pixelWidth, availableH / pixelHeight);
  const w = Math.max(1, Math.floor(pixelWidth * scale));
  const h = Math.max(1, Math.floor(pixelHeight * scale));
  const nextW = `${w}px`;
  const nextH = `${h}px`;
  if (box.style.width === nextW && box.style.height === nextH) return;
  box.style.width = nextW;
  box.style.height = nextH;
}

type FittedEditorPreviewProps = {
  previewUrl: string;
  backgroundColor: string;
  toneFilter?: string;
};

function FittedEditorPreview({ previewUrl, backgroundColor, toneFilter }: FittedEditorPreviewProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const fit = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;
    fitDisplayBoxToWrap(wrapRef.current, boxRef.current, img.naturalWidth, img.naturalHeight);
  }, []);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const ro = new ResizeObserver(() => fit());
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [fit, previewUrl]);

  return (
    <div className="mynazca-app__bg-wrap">
      <div ref={wrapRef} className="mynazca-app__bg-canvas-tone-wrap">
        <div ref={boxRef} className="mynazca-app__bg-canvas-display-box">
          <div
            className="mynazca-app__bg-canvas-tone-wrap__fill"
            aria-hidden
            style={{ backgroundColor }}
          />
          <img
            ref={imgRef}
            src={previewUrl}
            alt=""
            className="mynazca-app__fitted-preview-img"
            style={toneFilter ? { filter: toneFilter } : undefined}
            onLoad={() => fit()}
          />
        </div>
      </div>
    </div>
  );
}

export type MyNazcaImageEditorPreviewProps = {
  lang: 'ja' | 'en';
  workingPreviewUrl: string | null;
  openEditSection: EditSection | null;
  bgCompositeColor: string;
  toneFilter: string;
  cropperImgRef: RefObject<HTMLImageElement | null>;
  bgCanvasRef: RefObject<HTMLCanvasElement | null>;
  bgCanvasWrapRef: RefObject<HTMLDivElement | null>;
  bgCanvasDisplayBoxRef: RefObject<HTMLDivElement | null>;
  brushCursorPos: { x: number; y: number } | null;
  getBrushDisplayDiameter: (canvas: HTMLCanvasElement | null) => number;
  onBrushPointerEnter: (e: PointerEvent) => void;
  onBrushPointerLeave: () => void;
  onBgCanvasPointerDown: (e: PointerEvent) => void;
  onBgCanvasPointerMove: (e: PointerEvent) => void;
  onBgCanvasPointerUp: (e: PointerEvent) => void;
  className?: string;
};

export function MyNazcaImageEditorPreview({
  lang,
  workingPreviewUrl,
  openEditSection,
  bgCompositeColor,
  toneFilter,
  cropperImgRef,
  bgCanvasRef,
  bgCanvasWrapRef,
  bgCanvasDisplayBoxRef,
  brushCursorPos,
  getBrushDisplayDiameter,
  onBrushPointerEnter,
  onBrushPointerLeave,
  onBgCanvasPointerDown,
  onBgCanvasPointerMove,
  onBgCanvasPointerUp,
  className,
}: MyNazcaImageEditorPreviewProps) {
  const sectionClass = className
    ? `mynazca-app__section mynazca-app__section--image-editor ${className}`
    : 'mynazca-app__section mynazca-app__section--image-editor';

  return (
    <div className={sectionClass}>
      {workingPreviewUrl == null ? (
        <p className="mynazca-app__muted">
          {lang === 'ja' ? '画像を選択するとプレビューが表示されます。' : 'Select an image to see a preview.'}
        </p>
      ) : openEditSection === 'crop' ? (
        <div className="mynazca-app__cropper-wrap">
          <img
            ref={cropperImgRef}
            src={workingPreviewUrl}
            alt=""
            className="mynazca-app__cropper-img"
          />
        </div>
      ) : openEditSection === 'background' ? (
        <div className="mynazca-app__bg-wrap">
          <div ref={bgCanvasWrapRef} className="mynazca-app__bg-canvas-tone-wrap">
            <div ref={bgCanvasDisplayBoxRef} className="mynazca-app__bg-canvas-display-box">
              <div
                className="mynazca-app__bg-canvas-tone-wrap__fill"
                aria-hidden
                style={{ backgroundColor: bgCompositeColor }}
              />
              {brushCursorPos != null && (() => {
                const d = getBrushDisplayDiameter(bgCanvasRef.current);
                return (
                  <div
                    className="mynazca-app__brush-cursor"
                    aria-hidden
                    style={{
                      width: d,
                      height: d,
                      left: brushCursorPos.x - d / 2,
                      top: brushCursorPos.y - d / 2,
                    }}
                  />
                );
              })()}
              <canvas
                ref={bgCanvasRef}
                className="mynazca-app__bg-canvas mynazca-app__bg-canvas--with-tone"
                style={{ filter: toneFilter }}
                onPointerEnter={onBrushPointerEnter}
                onPointerLeave={onBrushPointerLeave}
                onPointerDown={onBgCanvasPointerDown}
                onPointerMove={onBgCanvasPointerMove}
                onPointerUp={onBgCanvasPointerUp}
              />
            </div>
          </div>
        </div>
      ) : (
        <FittedEditorPreview
          previewUrl={workingPreviewUrl}
          backgroundColor={bgCompositeColor}
          toneFilter={toneFilter}
        />
      )}
    </div>
  );
}
